import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  PlanType,
  StatusChipV4,
  TableComponentV2,
  useAuth,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { DateTime } from 'luxon'
import {
  isLoading,
  LoadingTypes,
  searchDropDownOptionMapping,
  standardLabraDateFormat,
  useAccountId,
  useCurrentCloudProvider,
  useScreenSize,
  ViewValueWithTruncate,
} from '@labrav/common'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFlag } from '@labrav/flags'
import { useDispatch, useSelector } from '../../../store'
import { getSubscriptions } from '../../../modules/subscription/action'
import { ShowExpirationDateWithIcon } from '../../../common/components/ShowExpirationDateWithIcon/ShowExpirationDateWithIcon'
import {
  subscriptionDisabled,
  meteringDisabled,
} from '../../../utils/subscriptionDisabled'
import { useStyles } from './SubscriptionsDataTable.styles'
import {
  type CloudMarketplace,
  Product,
  ProductStatus,
} from '../../../modules/productsListing/types'
import { getModalSubscriptions } from '../../../modules/modalSubscription/action'
import { useCommonStyles } from '../../../common/styles/common.styles'
import { ResponsiveColumn } from '../../../common/components/ResponsiveColumn/ResponsiveColumn'
import { providerToAuthCloud } from '@labrav/common'
import {
  subscriptionListSortToApiParams,
  subscriptionsRangeStatus,
  type SubscriptionFilter,
  type SubscriptionSortConfig,
} from '../../../utils/subscriptionsFilterMapping'
import { getStatusAndLabelForSubscription } from '../../../utils/subscriptionStatus'
import {
  getGcpContractStatusLabel,
  getGcpStatusChipVariant,
} from '../../../utils/gcpSubscriptionHelpers'
import { cmcsExternalPageUrl } from '../../../utils/constants'
import { SubscriptionObject } from '../../../modules/subscription/reducer'
import { get, isEmpty } from 'lodash'

const getContractStatusChipVariant = (
  label: string
): 'success' | 'error' | 'warning' => {
  if (label === 'Active') return 'success'
  if (label === 'Expired' || label === 'Ended') return 'error'
  return 'warning'
}

const renderSubscriptionContractStatus = (
  subscriptionItem: SubscriptionObject,
  cloud: string,
  treatPastExpiryAsExpired: boolean
) => {
  if (cloud === 'GCP') {
    const contractStatus = subscriptionItem.listingStatus
    if (!contractStatus) return '-'

    return (
      <StatusChipV4
        label={getGcpContractStatusLabel(contractStatus)}
        variant={getGcpStatusChipVariant(contractStatus)}
        size="small"
      />
    )
  }

  const statusLabel = getStatusAndLabelForSubscription(
    subscriptionItem.status,
    subscriptionItem.contractExpirationDate,
    DateTime.now,
    { treatPastExpiryAsExpired }
  )

  return (
    <StatusChipV4
      label={statusLabel.label}
      variant={getContractStatusChipVariant(statusLabel.label)}
      size="small"
    />
  )
}

export const OfferTypeMapping = {
  PRIVATE: 'Private offer',
  PUBLIC: 'Public offer',
  CPPO: 'Channel offer',
}

export const getSubscriptionActionMenuOptions = ({
  cloudMarketplace,
  buyerFormFilled,
  routeCloud,
  accountId,
  subscriptionItem,
  selectedProductDetails,
  selectedProductStatus,
}: {
  cloudMarketplace: CloudMarketplace
  buyerFormFilled: boolean
  routeCloud: string
  accountId: string
  subscriptionItem: SubscriptionObject
  selectedProductDetails: Product
  selectedProductStatus?: string
}) => {
  const allOptions = [
    {
      key: 'view-details',
      label: 'View details',
      route: `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionItem?.subscriptionId}/detail`,
    },
    {
      key: 'buyer-registration',
      label: 'Buyer registration form',
      route: `${cmcsExternalPageUrl}/buyer_form?buyer_id=${
        subscriptionItem?.buyerId
      }&product_name=${
        subscriptionItem?.productName || 'None'
      }&marketplace_url=None&buyer_exists=${buyerFormFilled}&labra_product_id=${
        subscriptionItem?.productId
      }`,
      disable: false,
    },
    {
      key: 'renew-subscription',
      label: 'Create agreement based offer',
      route: `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionItem?.subscriptionId}/edit`,
      disable: subscriptionDisabled(
        subscriptionItem?.productType,
        subscriptionItem?.status,
        selectedProductDetails?.status || selectedProductStatus,
        subscriptionItem
      ),
      subscriptionItem: subscriptionItem,
      disabledTooltip:
        subscriptionItem?.productType === PlanType.CONTAINER ||
        subscriptionItem?.productType === PlanType.AMI
          ? 'AWS does not support ABO creation for this product type.'
          : selectedProductDetails?.status ||
              selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED
            ? 'You cannot create ABO for restricted products.'
            : 'You can create ABO for active contract',
    },
    {
      key: 'quick-metering',
      label: 'Quick Metering',
      route: `/flyout/${routeCloud}/${accountId}/products-listing/${subscriptionItem?.productId}/contracts/${subscriptionItem?.subscriptionId}`,
      disable: subscriptionItem ? meteringDisabled(subscriptionItem) : true,
      disabledTooltip:
        'Metering allowed only for active SaaS purchases with usage based dimensions',
    },
  ]
  if (cloudMarketplace === 'GCP') {
    return [allOptions[0]]
  }
  return allOptions
}

export type SubscriptionsDataTableProps = {
  mapping: Record<string, string>
  pageNumber: number
  subscriptions: SubscriptionObject[]
  count?: number
  searchNow?: boolean
  inputValue?: string
  selectedOption?: string
  selectedItem?: string[]
  onChangeCheckbox?: (selected: string[]) => void
  showRadioButton?: boolean
  tableHeight?: string
  appliedFilteredValues?: SubscriptionFilter
  privateOfferId?: string
  selectedSubscription?: string
  handleSelectSubscription?: (option: ColumnMenuOption) => void
  type?: string
  modalCount?: number
  isSalesforce?: boolean
  salesforcePartnerId?: string
  sortConfig?: SubscriptionSortConfig
  onSortConfigChange?: (config: {
    key: string
    direction: 'asc' | 'desc'
  }) => void
}

export const SubscriptionsDataTable = ({
  mapping,
  pageNumber,
  subscriptions,
  count,
  searchNow,
  inputValue,
  selectedOption,
  selectedItem,
  onChangeCheckbox,
  showRadioButton = false,
  tableHeight,
  appliedFilteredValues,
  handleSelectSubscription,
  type,
  modalCount,
  isSalesforce = false,
  salesforcePartnerId,
  sortConfig = null,
  onSortConfigChange,
}: SubscriptionsDataTableProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { classes: commonClasses } = useCommonStyles()
  const { flag: contractStatusUpdate } = useFlag('contractStatusUpdate')
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    partner_id?: string
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = salesforcePartnerId || routeAccountId || fallbackAccountId
  const [selectedProductStatus, setSelectedProductStatus] = useState('')
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const onChangeRow = (selectedId: string) => {
    const selectedSubscriptionItem = subscriptions.find(
      sub => sub.subscriptionId === selectedId
    )
    setSelectedProductStatus(selectedSubscriptionItem?.productStatus as string)
    if (selectedSubscriptionItem && type !== 'MODAL') {
      navigate(
        `/flyout/${routeCloud}/${accountId}/subscriptions/${selectedSubscriptionItem?.subscriptionId}/detail`
      )
    }
  }

  const sortableFields = useMemo(
    () => [
      'buyerCompanyName',
      'productName',
      'privateOfferName',
      'contractExpirationDate',
      'contractStartDate',
      'status',
      'offerType',
    ],
    []
  )
  const handleSelectOption = (option: ColumnMenuOption) => {
    if (option?.key === 'buyer-registration') {
      window.open(option?.route, '_blank')
      return
    }

    if (option?.key === 'renew-subscription') {
      handleSelectSubscription && handleSelectSubscription(option)
      return
    }

    if (option?.route) {
      navigate(option?.route)
    }
  }
  const { selectedProduct: productId, products } =
    useFlyOutProductsBasedOnSelectedCloud()
  const selectedProductDetails = useMemo(() => {
    return (
      products.find(product => product.productId === productId) ||
      ({} as Product)
    )
  }, [products, productId])

  const { loading: authLoading, canDo } = useAuth()
  const canCreateABO = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(
          selectedCloudTypeProductListing.toLowerCase()
        ),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, selectedCloudTypeProductListing])

  const { screenWidth } = useScreenSize()
  const columnMaxWidth = useMemo(
    () => `${(screenWidth * (type === 'MODAL' ? 7 : 12)) / 100}px`,
    [screenWidth, type]
  )
  const columnMaxWidthSubscriptionId = useMemo(
    () => `${(screenWidth * 7) / 100}px`,
    [screenWidth]
  )

  const getActionMenuOptionsForRow = useCallback(
    (subscriptionItem: SubscriptionObject) => {
      const opts = getSubscriptionActionMenuOptions({
        cloudMarketplace: selectedCloudTypeProductListing as CloudMarketplace,
        buyerFormFilled: !!subscriptionItem?.buyerCompanyName,
        routeCloud: routeCloud || '',
        accountId: accountId || '',
        subscriptionItem,
        selectedProductDetails,
        selectedProductStatus,
      })

      return opts.map(opt => {
        if (
          opt.key === 'renew-subscription' ||
          opt.key === 'buyer-registration' ||
          opt.key === 'quick-metering'
        ) {
          const permissionBlocked = !canCreateABO
          return {
            ...opt,
            disable: opt.disable || permissionBlocked,
            disabledTooltip: permissionBlocked
              ? 'This action is disabled because you have read-only access.'
              : opt.disabledTooltip,
          }
        }

        return opt
      })
    },
    [
      accountId,
      canCreateABO,
      routeCloud,
      selectedProductDetails,
      selectedProductStatus,
      selectedCloudTypeProductListing,
    ]
  )

  const visibleSubscriptions = useMemo(() => {
    const rows = subscriptions || []
    if (
      !contractStatusUpdate?.value ||
      selectedCloudTypeProductListing === 'GCP' ||
      appliedFilteredValues?.status !== subscriptionsRangeStatus.ACTIVE
    ) {
      return rows
    }
    return rows.filter(
      s =>
        getStatusAndLabelForSubscription(
          s.status,
          s.contractExpirationDate,
          DateTime.now,
          { treatPastExpiryAsExpired: true }
        ).label !== 'Expired'
    )
  }, [
    subscriptions,
    appliedFilteredValues?.status,
    selectedCloudTypeProductListing,
    contractStatusUpdate?.value,
  ])

  const subscriptionsObject =
    visibleSubscriptions.length > 0
      ? visibleSubscriptions.map((subscriptionItem: SubscriptionObject) => ({
          id: subscriptionItem?.subscriptionId,
          isDisabled:
            subscriptionDisabled(
              subscriptionItem?.productType,
              subscriptionItem?.status,
              selectedProductDetails?.status || selectedProductStatus,
              subscriptionItem
            ) ||
            (isSalesforce && subscriptionItem?.offerType === 'CPPO'),
          buyerCompanyName: (
            <div className={classes.textCellWrapper}>
              <Typography
                className={classes.buyerName}
                style={{ maxWidth: columnMaxWidth }}
              >
                <ViewValueWithTruncate
                  value={subscriptionItem?.buyerCompanyName || '-'}
                  showIcon
                />
              </Typography>
            </div>
          ),
          productName: (
            <ResponsiveColumn
              linkText={subscriptionItem?.productName || ''}
              linkUrl={subscriptionItem?.productUrl || ''}
              maxWidth={columnMaxWidth}
            />
          ),
          privateOfferName: (
            <ResponsiveColumn
              linkText={subscriptionItem?.privateOfferName || '-'}
              linkUrl={subscriptionItem?.labraOfferUrl || ''}
              maxWidth={columnMaxWidth}
            />
          ),
          subscriptionId: (
            <ResponsiveColumn
              linkText={subscriptionItem?.awsAgreementId || '-'}
              maxWidth={columnMaxWidthSubscriptionId}
            />
          ),
          contractStartDate: subscriptionItem?.contractStartDate
            ? DateTime.fromISO(subscriptionItem.contractStartDate).toFormat(
                standardLabraDateFormat
              )
            : '-',
          contractExpirationDate: subscriptionItem?.contractExpirationDate ? (
            <ShowExpirationDateWithIcon
              date={subscriptionItem.contractExpirationDate}
              shouldShowExpired
              {...(selectedCloudTypeProductListing === 'GCP'
                ? {
                    expirePrefix: 'Ends in',
                    daysToCompare: 30,
                    variant: 'split' as const,
                  }
                : {})}
            />
          ) : (
            '-'
          ),
          status: renderSubscriptionContractStatus(
            subscriptionItem,
            selectedCloudTypeProductListing,
            !!contractStatusUpdate?.value
          ),
          offerType: subscriptionItem?.offerType
            ? get(OfferTypeMapping, subscriptionItem?.offerType)
            : '-',
          offerAcceptedAt: subscriptionItem?.offerAcceptedAt
            ? DateTime.fromISO(subscriptionItem.offerAcceptedAt).toFormat(
                standardLabraDateFormat
              )
            : '-',
          action: (
            <div className={commonClasses.actionColumn} data-prevent-row-click>
              <ColumnActionWithMenuV2
                handleSelectOption={handleSelectOption}
                menuOptions={getActionMenuOptionsForRow(subscriptionItem)}
              />
            </div>
          ),
        }))
      : []

  const { pageSize: pageSizeValue } = useScreenSize()
  const subscriptionListSortParams = useMemo(
    () =>
      subscriptionListSortToApiParams(
        selectedCloudTypeProductListing === 'GCP' ? sortConfig : undefined
      ),
    [sortConfig, selectedCloudTypeProductListing]
  )

  const fetchMoreData = useCallback(() => {
    if (type === 'MODAL') {
      if (!isEmpty(inputValue) && !isEmpty(selectedOption)) {
        dispatch(
          getModalSubscriptions({
            cloudMarketplace: selectedCloudTypeProductListing,
            partnerId: accountId,
            productId,
            pageNumber: pageNumber + 1,
            pageSize: pageSizeValue,
            input: inputValue,
            selectedInput: (
              searchDropDownOptionMapping.subsciptionsOptionMapping as Record<
                string,
                string
              >
            )[selectedOption || 'Company'],
            sectionName: 'agreements',
            ...subscriptionListSortParams,
          })
        )
      } else {
        dispatch(
          getModalSubscriptions({
            cloudMarketplace: selectedCloudTypeProductListing,
            partnerId: accountId,
            pageNumber: pageNumber + 1,
            pageSize: pageSizeValue,
            productId,
            status: appliedFilteredValues?.status,
            expiringDays: appliedFilteredValues?.expiringDays,
            ...subscriptionListSortParams,
          })
        )
      }
    } else {
      if (!isEmpty(inputValue) && !isEmpty(selectedOption)) {
        dispatch(
          getSubscriptions({
            cloudMarketplace: selectedCloudTypeProductListing,
            partnerId: accountId,
            productId,
            pageNumber: pageNumber + 1,
            pageSize: pageSizeValue,
            input: inputValue,
            selectedInput: (
              searchDropDownOptionMapping.subsciptionsOptionMapping as Record<
                string,
                string
              >
            )[selectedOption || 'Company'],
            sectionName: 'agreements',
            contractStatusUpdate: !!contractStatusUpdate?.value,
            ...subscriptionListSortParams,
          })
        )
      } else {
        dispatch(
          getSubscriptions({
            cloudMarketplace: selectedCloudTypeProductListing,
            partnerId: accountId,
            pageNumber: pageNumber + 1,
            pageSize: pageSizeValue,
            productId,
            status: appliedFilteredValues?.status,
            expiringDays: appliedFilteredValues?.expiringDays,
            contractStatusUpdate: !!contractStatusUpdate?.value,
            ...subscriptionListSortParams,
          })
        )
      }
    }
  }, [
    accountId,
    pageNumber,
    productId,
    searchNow,
    appliedFilteredValues,
    selectedCloudTypeProductListing,
    pageSizeValue,
    type,
    dispatch,
    inputValue,
    selectedOption,
    subscriptionListSortParams,
    contractStatusUpdate?.value,
  ])
  const updatedCount = useMemo(() => {
    return type === 'MODAL' ? modalCount : count
  }, [type, modalCount, count])

  return (
    <>
      {searchNow && selectedCloudTypeProductListing === 'AWS' ? (
        <>
          {updatedCount === 0 && !loading ? (
            <div className={classes.noResultHeaderContainer}>
              <Typography className={classes.noResultHeading}>
                No results found.
              </Typography>
              <Typography className={classes.noResultSubHeading}>
                We couldn't find any results matching your search. Please try
                using different keywords or check your spelling.
              </Typography>
            </div>
          ) : (
            <>
              <div className={classes.resultHeadingContainer}>
                {(updatedCount as number) > 0 && (
                  <Typography className={classes.resultHeading}>
                    Showing {updatedCount} search result
                    {updatedCount !== 1 && 's'}.
                  </Typography>
                )}
              </div>
              <TableComponentV2
                keyName={'migrationDetails.products'}
                objects={subscriptionsObject}
                mapping={mapping}
                count={updatedCount}
                fetchMoreData={fetchMoreData}
                selectedItem={selectedItem || []}
                onChangeCheckbox={
                  onChangeCheckbox ? onChangeCheckbox : () => void 0
                }
                isLoading={loading}
                showRadioButton={showRadioButton}
                showCheckbox={false}
                tableHeight={tableHeight}
                onChangeRow={onChangeRow}
                customColumns={['action']}
                stickyColumns={['action']}
                customCellProps={[
                  {},
                  { style: { width: columnMaxWidth } },
                  { style: { width: columnMaxWidth } },
                  { style: { width: columnMaxWidthSubscriptionId } },
                ]}
              />
            </>
          )}
        </>
      ) : (
        <TableComponentV2
          keyName={'migrationDetails.products'}
          objects={subscriptionsObject}
          mapping={mapping}
          count={updatedCount}
          fetchMoreData={fetchMoreData}
          selectedItem={selectedItem || []}
          onChangeCheckbox={onChangeCheckbox ? onChangeCheckbox : () => void 0}
          isLoading={loading}
          showRadioButton={showRadioButton}
          showCheckbox={false}
          tableHeight={tableHeight}
          onChangeRow={onChangeRow}
          customColumns={['action']}
          stickyColumns={['action']}
          customCellProps={[
            {},
            { style: { width: columnMaxWidth } },
            { style: { width: columnMaxWidth } },
            { style: { width: columnMaxWidthSubscriptionId } },
          ]}
          {...(selectedCloudTypeProductListing === 'GCP' && {
            sortableFields,
            onSortChange: onSortConfigChange
              ? (field: string, direction: 'asc' | 'desc') =>
                  onSortConfigChange({ key: field, direction })
              : undefined,
          })}
        />
      )}
    </>
  )
}

export type { SubscriptionFilter, SubscriptionSortConfig }
